import { Alert, Box, Button, Stack, Typography } from "@mui/material"
import { useState } from "react"
import { Comparison } from "routes/schedule/compare/lib/diff3"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import { ChangeLegislationList } from "./change-legislation-list"
import { ChangeItemEditor } from "./change-item-editor"
import { ProposedTaskCard } from "./proposed-task-card"
import { FrequencyChangeDisplay } from "./frequency-change-display"
import { ChangeSourceCitationList } from "./change-source-citation-list"
import { shouldRenderProposedRichTextComparison } from "./change-item-body-presenters"

export function ChangeItemBody({ change, onTextChange, onProposedTaskChange }) {
    const [editing, setEditing] = useState(false)
    const shouldRenderProposedRichText = shouldRenderProposedRichTextComparison(change)

    return (
        <>
            <Alert severity="info" icon={<Iconify icon="mdi:information-outline" />}>
                <Typography variant="body2">{change.rationale}</Typography>
            </Alert>

            <If truthy={change.legislationExcerpt}>
                <Box
                    sx={{
                        borderLeft: "3px solid",
                        borderLeftColor: "info.light",
                        pl: 2,
                        py: 0.5,
                        bgcolor: "action.hover",
                        borderRadius: "0 4px 4px 0",
                    }}
                >
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Legislation excerpt
                    </Typography>
                    <Typography variant="body2" fontStyle="italic">
                        "{change.legislationExcerpt}"
                    </Typography>
                </Box>
            </If>

            <ChangeLegislationList legislation={change.legislation} />
            <ChangeSourceCitationList citations={change.sourceCitations} />

            <If truthy={change.oldText != null && change.newText != null}>
                <Stack direction="row" spacing={2} sx={{ "& > *": { flex: 1 } }}>
                    <Box>
                        <Stack direction="row" alignItems="center" sx={{ minHeight: 28, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Current
                            </Typography>
                        </Stack>
                        <Box
                            className="styled-bullets"
                            sx={{
                                border: "1px solid",
                                borderColor: "action.disabled",
                                borderRadius: 1,
                                p: 1.5,
                                minHeight: 48,
                                fontSize: "0.875rem",
                            }}
                        >
                            <If truthy={change.oldText === ""}>
                                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                    Currently no note
                                </Typography>
                                <Else>
                                    <Comparison
                                        text={change.oldText}
                                        original={change.newText}
                                        other={change.newText}
                                    />
                                </Else>
                            </If>
                        </Box>
                    </Box>
                    <Box>
                        <If truthy={editing}>
                            <ChangeItemEditor
                                initialText={change.newText}
                                onSave={handleTextSave}
                                onCancel={cancelEdit}
                            />
                            <Else>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 28, mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Proposed
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={startEdit}
                                        sx={{ minWidth: 0, p: 0.25, fontSize: "0.7rem" }}
                                        startIcon={
                                            <Iconify icon="mdi:pencil-outline" sx={{ fontSize: "14px !important" }} />
                                        }
                                    >
                                        Edit
                                    </Button>
                                </Stack>
                                <Box
                                    className="styled-bullets"
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "action.disabled",
                                        borderRadius: 1,
                                        p: 1.5,
                                        minHeight: 48,
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    <If truthy={change.oldText === ""}>
                                        <If truthy={shouldRenderProposedRichText}>
                                            <Comparison text={change.newText} original="" other="" />
                                            <Else>
                                                <Typography variant="body2">{change.newText}</Typography>
                                            </Else>
                                        </If>
                                        <Else>
                                            <Comparison
                                                text={change.newText}
                                                original={change.oldText}
                                                other={change.oldText}
                                            />
                                        </Else>
                                    </If>
                                </Box>
                            </Else>
                        </If>
                    </Box>
                </Stack>
            </If>

            <If truthy={change.changeType === "remove_task" && change.oldText != null}>
                <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Task to be removed
                    </Typography>
                    <Box
                        className="styled-bullets"
                        sx={{
                            border: "1px solid",
                            borderColor: "error.light",
                            borderRadius: 1,
                            p: 1.5,
                            bgcolor: "error.lighter",
                            fontSize: "0.875rem",
                        }}
                    >
                        {change.oldText}
                    </Box>
                </Box>
            </If>

            <If truthy={change.changeType === "task_frequency" && change.newFrequency}>
                <FrequencyChangeDisplay change={change} />
            </If>

            <If truthy={change.changeType === "add_task"}>
                <If truthy={change.proposedTask}>
                    <ProposedTaskCard proposedTask={change.proposedTask} onTaskChange={handleProposedTaskChange} />
                    <Else>
                        <If truthy={change.newText != null}>
                            <If truthy={editing}>
                                <ChangeItemEditor
                                    initialText={change.newText}
                                    onSave={handleTextSave}
                                    onCancel={cancelEdit}
                                />
                                <Else>
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                            <Typography variant="caption" color="text.secondary">
                                                New task to be added
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={startEdit}
                                                sx={{ minWidth: 0, p: 0.25, fontSize: "0.7rem" }}
                                                startIcon={
                                                    <Iconify
                                                        icon="mdi:pencil-outline"
                                                        sx={{ fontSize: "14px !important" }}
                                                    />
                                                }
                                            >
                                                Edit
                                            </Button>
                                        </Stack>
                                        <Box
                                            className="styled-bullets"
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "success.light",
                                                borderRadius: 1,
                                                p: 1.5,
                                                bgcolor: "success.lighter",
                                                fontSize: "0.875rem",
                                                fontFamily: "monospace",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {change.newText}
                                        </Box>
                                    </Box>
                                </Else>
                            </If>
                        </If>
                    </Else>
                </If>
            </If>
        </>
    )

    function startEdit() {
        setEditing(true)
    }

    function cancelEdit() {
        setEditing(false)
    }

    function handleTextSave(newText) {
        setEditing(false)
        onTextChange?.(change.id, newText)
    }

    function handleProposedTaskChange(updatedTask) {
        onProposedTaskChange?.(change.id, updatedTask)
    }
}
